<template>
  <div class="w-full max-w-md mx-auto">
    <div class="text-center mb-8">
      <h2 class="text-3xl font-bold text-gray-900">로그인</h2>
      <p class="mt-2 text-gray-600">계정에 로그인하여 Seatisfy를 이용하세요</p>
    </div>

    <div class="space-y-6">
      <!-- 이메일 -->
      <div>
        <label for="email" class="form-label">이메일</label>
        <input
          id="email"
          v-model="form.email"
          type="email"
          required
          autocomplete="email"
          class="form-input"
          :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.email }"
          placeholder="example@email.com"
        />
        <p v-if="errors.email" class="form-error">{{ errors.email }}</p>
      </div>

      <!-- 비밀번호 -->
      <div>
        <label for="password" class="form-label">비밀번호</label>
        <div class="relative">
          <input
            id="password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            required
            autocomplete="current-password"
            class="form-input pr-10"
            :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.password }"
            placeholder="비밀번호를 입력하세요"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          >
            <EyeIcon v-if="!showPassword" class="w-5 h-5" />
            <EyeSlashIcon v-else class="w-5 h-5" />
          </button>
        </div>
        <p v-if="errors.password" class="form-error">{{ errors.password }}</p>
      </div>

      <!-- 로그인 유지 & 비밀번호 찾기 -->
      <div class="flex items-center justify-between">
        <label class="flex items-center">
          <input
            v-model="rememberMe"
            type="checkbox"
            class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span class="ml-2 text-sm text-gray-600">로그인 유지</span>
        </label>
        <RouterLink
          to="/forgot-password"
          class="text-sm text-primary-600 hover:text-primary-500"
        >
          비밀번호를 잊으셨나요?
        </RouterLink>
      </div>

      <!-- 로그인 버튼 -->
<!--      type="submit"-->
      <button
        :disabled="isLoading"
        class="btn btn-primary btn-lg w-full"
        v-on:click="handleSubmit"
      >
        <span v-if="isLoading" class="flex items-center justify-center">
          <div class="spinner w-5 h-5 mr-2"></div>
          로그인 중...
        </span>
        <span v-else>로그인</span>
      </button>

      <!-- 일반적인 에러 메시지 -->
      <div v-if="errors.general" class="bg-red-50 border border-red-200 rounded-md p-3">
        <p class="text-red-600 text-sm">{{ errors.general }}</p>
      </div>
    </div>

    <!-- 회원가입 링크 -->
    <div class="mt-6 text-center">
      <p class="text-gray-600">
        아직 계정이 없으신가요?
        <RouterLink
          to="/register"
          class="text-primary-600 hover:text-primary-500 font-medium"
        >
          회원가입
        </RouterLink>
      </p>
    </div>

    <!-- 데모 계정 안내 -->
    <div class="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h4 class="text-sm font-medium text-blue-900 mb-3">데모 계정으로 체험하기</h4>
      <div class="space-y-3">
        <!-- 일반 사용자 -->
        <div class="flex items-center justify-between bg-white rounded-md p-3 border border-blue-100">
          <div class="text-sm text-blue-700">
            <div class="font-semibold text-blue-900 mb-1">일반 사용자</div>
            <div class="text-xs">user@seatisfy.com / user1234</div>
          </div>
          <button
            type="button"
            @click="fillDemoAccount('user')"
            class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            사용
          </button>
        </div>

        <!-- 관리자 -->
        <div class="flex items-center justify-between bg-white rounded-md p-3 border border-blue-100">
          <div class="text-sm text-blue-700">
            <div class="font-semibold text-blue-900 mb-1">관리자</div>
            <div class="text-xs">admin@seatisfy.com / admin123</div>
          </div>
          <button
            type="button"
            @click="fillDemoAccount('admin')"
            class="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            사용
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import type { LoginRequest } from '@/types'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const showPassword = ref(false)
const rememberMe = ref(false)
const isLoading = computed(() => authStore.isLoading)

// 폼 데이터
const form = reactive<LoginRequest>({
  email: '',
  password: ''
})

// 에러 상태
const errors = reactive({
  email: '',
  password: '',
  general: ''
})

// 에러 초기화
const clearErrors = () => {
  errors.email = ''
  errors.password = ''
  errors.general = ''
}

// 폼 유효성 검사
const validateForm = (): boolean => {
  clearErrors()
  let isValid = true

  if (!form.email) {
    errors.email = '이메일을 입력해주세요.'
    isValid = false
  } else if (!/\S+@\S+\.\S+/.test(form.email)) {
    errors.email = '올바른 이메일 형식을 입력해주세요.'
    isValid = false
  }

  if (!form.password) {
    errors.password = '비밀번호를 입력해주세요.'
    isValid = false
  } else if (form.password.length < 6) {
    errors.password = '비밀번호는 6자 이상이어야 합니다.'
    isValid = false
  }

  return isValid
}

// 로그인 처리
const handleSubmit = async () => {
  if (!validateForm()) return

  try {
    const success = await authStore.login(form)
    if (success) {
      // 리다이렉트 경로가 있으면 해당 경로로, 없으면 홈으로
      const redirectTo = (route.query.redirect as string) || '/'
      router.push(redirectTo)
    }
  } catch (error: any) {
    if (error.response?.status === 401) {
      errors.general = '이메일 또는 비밀번호가 올바르지 않습니다.'
    } else {
      errors.general = '로그인 중 오류가 발생했습니다. 다시 시도해주세요.'
    }
  }
}

// 데모 계정 자동 입력
const fillDemoAccount = (type: 'user' | 'admin') => {
  if (type === 'user') {
    form.email = 'user@seatisfy.com'
    form.password = 'user1234'
  } else {
    form.email = 'admin@seatisfy.com'
    form.password = 'admin123'
  }
  clearErrors()
}
</script>
